import { useState } from "react";
import type { Lang } from "../types.ts";
import type { SyncApi } from "../state/useSync.ts";
import { t } from "../i18n/ui.ts";
import type { UiStrings } from "../i18n/ui.ts";

/** Maps a server error code onto a translated message. */
const ERRORS: Record<string, keyof UiStrings> = {
  invalid_credentials: "errInvalidCredentials",
  username_taken: "errUsernameTaken",
  password_too_short: "errPasswordTooShort",
  too_many_attempts: "errTooManyAttempts",
  network_error: "errNetwork",
};

export function AccountView({ lang, sync }: { lang: Lang; sync: SyncApi }) {
  const [mode, setMode] = useState<"signIn" | "signUp">("signIn");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (sync.user) {
    return (
      <section className="account" aria-labelledby="account-title">
        <h2 id="account-title">{t(lang, "account")}</h2>
        <p className="account__who">{t(lang, "signedInAs", { name: sync.user.username })}</p>

        <p className="account__status" aria-live="polite">
          {sync.status === "syncing"
            ? t(lang, "syncSyncing")
            : sync.lastSyncedAt
              ? t(lang, "syncAt", {
                  time: new Date(sync.lastSyncedAt).toLocaleTimeString(),
                })
              : t(lang, "syncNever")}
        </p>

        {sync.error && sync.status === "error" && (
          <p className="account__error" role="alert">
            {t(lang, ERRORS[sync.error] ?? "errGeneric")}
          </p>
        )}

        <p className="account__hint">{t(lang, "accountLanHint")}</p>

        <div className="account__actions">
          <button type="button" className="btn btn--primary" onClick={() => void sync.syncNow()}>
            {t(lang, "syncNow")}
          </button>
          <button type="button" className="btn" onClick={() => void sync.signOut()}>
            {t(lang, "signOut")}
          </button>
        </div>
      </section>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const run = mode === "signIn" ? sync.signIn : sync.signUp;
    const ok = await run(username.trim(), password);
    setBusy(false);
    if (ok) setPassword("");
  };

  return (
    <section className="account" aria-labelledby="account-title">
      <h2 id="account-title">{t(lang, mode === "signIn" ? "signIn" : "signUp")}</h2>
      <p className="account__intro">{t(lang, "accountIntro")}</p>

      <form className="account__form" onSubmit={submit}>
        <label className="field">
          <span className="field__label">{t(lang, "username")}</span>
          <input
            type="text"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">{t(lang, "password")}</span>
          <input
            type="password"
            name="password"
            autoComplete={mode === "signIn" ? "current-password" : "new-password"}
            required
            minLength={mode === "signUp" ? 8 : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {mode === "signUp" && (
            <span className="field__hint">{t(lang, "passwordRule")}</span>
          )}
        </label>

        {sync.error && (
          <p className="account__error" role="alert">
            {t(lang, ERRORS[sync.error] ?? "errGeneric")}
          </p>
        )}

        <button type="submit" className="btn btn--primary" disabled={busy}>
          {t(lang, mode === "signIn" ? "signIn" : "signUp")}
        </button>
      </form>

      <button
        type="button"
        className="btn btn--ghost"
        onClick={() => setMode(mode === "signIn" ? "signUp" : "signIn")}
      >
        {t(lang, mode === "signIn" ? "needAccount" : "haveAccount")}
      </button>

      <p className="account__hint">{t(lang, "accountNoServer")}</p>
    </section>
  );
}
